import { EMPTY_OBJECT_ID } from "modules/common/constants";
import { DetailsPopup } from "modules/common/components/form/DetailsPopup";
import { Block } from "modules/common/components/page/Block";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { Search } from "modules/common/components/form/Search";
import { routes } from "modules/common/routes";
import { texts } from "modules/common/texts";
import { BankDetailsDetails } from "modules/dictionaries/bank-details/components/details/BankDetailsDetails";
import { BankDetailsList } from "modules/dictionaries/bank-details/components/list/BankDetailsList";
import { CompanyBankDetailsSnapshotType } from "modules/dictionaries/bank-details/models/bank-details";
import { CompanyBankDetailsStoreType } from "modules/dictionaries/bank-details/models/bank-details-store";
import { ProjectPortfolioSnapshotType } from "modules/dictionaries/project-portfolios/models/project-portfolio";
import { observer } from "mobx-react";
import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router";
import { matchRoute } from "modules/common/services/routing";
import { WhenPath } from "modules/common/components/routing/WhenPath";
import styles from "./AccountsPanel.module.scss";
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate";

const getSelectedAccountId = (pathname: string) => {
  const match = matchRoute<{ detailsId: string }>(pathname, routes.spending.organisations.accountDetails());

  return match && match.params ? match.params.detailsId : "";
};

/**
 * Правая панель страницы «Организации» — счета на оплату выбранной слева организации.
 */
export const AccountsPanel = observer(({ store, organisation, canChange }: AccountsPanelProps) => {
  const navigate = useStableNavigate();
  const { pathname } = useLocation();
  const accountId = getSelectedAccountId(pathname);

  // карточка следует за адресом: открыли другой счёт — загружаем его
  useEffect(() => {
    store.details.load(accountId);
  }, [store.details, accountId]);

  // список счетов всегда принадлежит выбранной организации
  useEffect(() => {
    store.list.setPortfolio(organisation ? organisation.id : "");

    if (organisation) {
      store.list.load();
    }
  }, [store.list, organisation]);

  const onAccountSelected = useCallback(
    (id: string = "") => {
      const url = id ? routes.spending.organisations.accountDetails(id) : routes.spending.organisations.path;

      navigate(url);
    },
    [navigate]
  );

  const onAccountAdd = useCallback(() => onAccountSelected(EMPTY_OBJECT_ID), [onAccountSelected]);

  const onAccountSave = useCallback(
    (values: CompanyBankDetailsSnapshotType) => {
      // счёт всегда заводится под выбранную организацию
      const owner = organisation ? { id: organisation.id, name: organisation.name } : values.projectPortfolio;

      return store.details.save({ ...values, projectPortfolio: owner ?? null });
    },
    [store.details, organisation]
  );

  const onAccountSaved = useCallback(() => {
    onAccountSelected("");
    store.list.load();
  }, [onAccountSelected, store.list]);

  const onAccountRemoved = useCallback(() => {
    store.list.load();
    navigate(routes.spending.organisations.path);
  }, [navigate, store.list]);

  const onAccountRemove = useCallback(async () => {
    const ok = await store.details.delete();

    if (ok) {
      onAccountRemoved();
    }

    return ok;
  }, [store.details, onAccountRemoved]);

  return (
    <Block columned={true} className={styles.page}>
      <WhenPath path={routes.spending.organisations.accountDetails()}>
        <DetailsPopup title="Счёт на оплату" onCancel={onAccountSelected}>
          <BankDetailsDetails
            canChange={canChange}
            bankDetails={store.details}
            onSave={onAccountSave}
            onSaved={onAccountSaved}
            onRemove={onAccountRemove}
          />
        </DetailsPopup>
      </WhenPath>

      <span className={styles.topSpan}>Счета на оплату</span>

      {!organisation && <div className={styles.hint}>Выберите организацию слева</div>}

      {canChange && organisation && (
        <PlanrButton
          type="secondary"
          size="small"
          icon="general-plus-big"
          onClick={onAccountAdd}
          style={{
            width: "230px",
            marginBottom: "32px",
            borderRadius: "120px",
          }}
        >{`${texts.add} счёт`}</PlanrButton>
      )}

      <div className={styles.right}>
        <Search query={store.list.query} onSearch={store.list.setQuery} />
      </div>

      <BankDetailsList
        store={store.list}
        onBankDetailsSelected={onAccountSelected}
        onBankDetailsRemoved={onAccountRemoved}
      />
    </Block>
  );
});

interface AccountsPanelProps {
  store: CompanyBankDetailsStoreType;
  organisation: ProjectPortfolioSnapshotType | null;
  canChange: boolean;
}
